import { Box, Icon } from '@hover/blueprint';
import { iDownload } from '@hover/icons';
import { captureException } from '@sentry/react';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import React, { useCallback } from 'react';

import { Button } from 'src/shared/components';
import { request } from 'src/shared/helpers/request';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

const DEFAULT_DOWNLOAD_NAME = 'HOVER Rendering';

export const saveToZip = (
  imageUrls: string[],
  fileName = DEFAULT_DOWNLOAD_NAME,
) => {
  const zip = new JSZip();
  const folder = zip.folder(fileName);
  imageUrls.forEach((imageUrl: string) => {
    const imagePromise = request(imageUrl).then(r => r.blob());
    const name = imageUrl.split('/').pop() || '';
    folder?.file(name, imagePromise);
  });

  zip
    .generateAsync({ type: 'blob' })
    .then(blob => saveAs(blob, fileName))
    .catch(e => captureException(e));
};

const DownloadImage = ({
  imageUrl,
  iconOnly,
  color = 'primary.base',
  translationKey = 'renderings.downloadImage',
}: {
  imageUrl?: string;
  iconOnly?: boolean;
  color?: string;
  translationKey?: string;
}) => {
  const t = useTranslate(TranslationNamespace.SHARED);

  const downloadImage = useCallback(async () => {
    if (imageUrl) {
      try {
        const imageName = imageUrl.split('/').pop();
        const image = await request(imageUrl);
        const imageBlob = await image.blob();
        saveAs(imageBlob, imageName);
      } catch (error) {
        captureException(error);
      }
    }
  }, [imageUrl]);

  return (
    <Box alignItems="center">
      <Button
        fill="minimal"
        fontSize="200"
        fontWeight="400"
        iconAfter={iconOnly ? undefined : iDownload}
        onClick={downloadImage}
        shape={iconOnly ? 'square' : 'box'}
        sx={{ color }}
      >
        {!iconOnly && t(translationKey as any)}
        {iconOnly && <Icon icon={iDownload} />}
      </Button>
    </Box>
  );
};

export { DownloadImage };
